import React, { useState, useEffect, useContext, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useForm, Controller } from 'react-hook-form';
import {
  Box,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  Chip,
  TextField,
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { AuthContext } from 'context/Auth';
import { db } from 'services/firebase';
import ConfirmDialog from 'components/common/ConfirmDialog';
import MasterChartBackButton from 'components/dashboard/healthRecords/MasterChartBackButton';
import DashboardPageHeader from 'components/common/DashboardPageHeader';
import DataLoadErrorAlert from 'components/common/DataLoadErrorAlert';
import {
  RecordActions,
  RecordCard,
  RecordCardStack,
  joinDetail,
} from 'components/dashboard/healthRecords/RecordList';

// Stored as typed, so these stay as they are rather than following the language.
const COMMON_PROCEDURES = [
  'Appendectomy', 'Cholecystectomy', 'Cesarean Section', 'Hysterectomy',
  'Knee Replacement', 'Hip Replacement', 'Coronary Artery Bypass (CABG)',
  'Hernia Repair', 'Tonsillectomy', 'Cataract Surgery', 'Colonoscopy',
  'Endoscopy', 'Laparoscopy', 'Spinal Fusion', 'Mastectomy',
];

const SurgicalHistoryPage = () => {
  const { t } = useTranslation('masterchart');
  const { user } = useContext(AuthContext);

  const [surgeries, setSurgeries] = useState([]);
  const [fullHealthRecords, setFullHealthRecords] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [loadError, setLoadError] = useState(false);
  // Translation keys rather than text (successMsg: [key, values]), so a language
  // switch re-renders them.
  const [successMsg, setSuccessMsg] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingIndex, setEditingIndex] = useState(-1);
  const [pendingDelete, setPendingDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const { control, handleSubmit, reset, setValue } = useForm({
    defaultValues: { procedure: '', year: '', hospital: '', notes: '' },
  });

  const load = useCallback(async () => {
    if (!user?.uid) return;
    setLoading(true);
    setLoadError(false);
    try {
      const doc = await db.collection('users').doc(user.uid).get();
      const hr = doc.data()?.healthRecords || {};
      setFullHealthRecords(hr);
      setSurgeries(hr.psh || []);
    } catch {
      // An empty list here would read as "none", and adding to it would replace
      // the stored list, so the list and Add stay hidden until a retry succeeds.
      setLoadError(true);
    } finally {
      setLoading(false);
    }
  }, [user?.uid]);

  useEffect(() => {
    load();
  }, [load]);

  const saveToFirestore = useCallback(
    async (updated) => {
      const merged = {
        ...(fullHealthRecords ?? {}),
        psh: updated,
        updatedAt: new Date().toISOString(),
      };
      await db
        .collection('users')
        .doc(user.uid)
        .set({ healthRecords: merged }, { merge: true });
      setFullHealthRecords(merged);
    },
    [user?.uid, fullHealthRecords]
  );

  const openDialog = (index = -1) => {
    setEditingIndex(index);
    if (index >= 0) {
      const s = surgeries[index];
      reset({
        procedure: s.procedure ?? '',
        year: s.year ?? '',
        hospital: s.hospital ?? '',
        notes: s.notes ?? '',
      });
    } else {
      reset({ procedure: '', year: '', hospital: '', notes: '' });
    }
    setDialogOpen(true);
  };

  const onDialogSubmit = handleSubmit(async (data) => {
    setSaving(true);
    setError(null);
    try {
      const toSave = {
        procedure: data.procedure.trim(),
        year: data.year ? String(data.year).trim() : '',
        hospital: data.hospital.trim(),
        notes: data.notes.trim(),
      };
      const updated = [...surgeries];
      if (editingIndex >= 0) {
        updated[editingIndex] = toSave;
      } else {
        updated.push(toSave);
      }
      updated.sort((a, b) => (parseInt(b.year) || 0) - (parseInt(a.year) || 0));
      await saveToFirestore(updated);
      setSurgeries(updated);
      setDialogOpen(false);
      setSuccessMsg(editingIndex >= 0 ? ['surgical.updated'] : ['surgical.added']);
    } catch {
      setError('common.failedSave');
    } finally {
      setSaving(false);
    }
  });

  const handleDelete = async () => {
    setDeleting(true);
    try {
      const updated = surgeries.filter((_, i) => i !== pendingDelete.index);
      setSurgeries(updated);
      await saveToFirestore(updated);
      setSuccessMsg(['common.removed', { label: pendingDelete.label }]);
      setPendingDelete(null);
    } catch {
      setError('common.failedRemove');
      setPendingDelete(null);
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className="inner">
      <DashboardPageHeader
        title={t('surgical.title')}
        subtitle={t('surgical.subtitle')}
      />

      {loadError && <DataLoadErrorAlert message={t('surgical.loadError')} onRetry={load} />}
      {error && (
        <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2 }}>
          {t(error)}
        </Alert>
      )}
      {successMsg && (
        <Alert severity="success" onClose={() => setSuccessMsg(null)} sx={{ mb: 2 }}>
          {t(...successMsg)}
        </Alert>
      )}

      {!loadError && (
        <>

          <RecordCardStack loading={loading}>
            {surgeries.map((s, i) => (
              <RecordCard
                key={i}
                title={s.procedure}
                detail={joinDetail(s.year, s.hospital, s.notes)}
                onEdit={() => openDialog(i)}
                onDelete={() => setPendingDelete({ index: i, label: s.procedure })}
                editLabel={t('surgical.editAria', { name: s.procedure })}
                deleteLabel={t('surgical.deleteAria', { name: s.procedure })}
              />
            ))}
          </RecordCardStack>

          <RecordActions
            addLabel={t('surgical.addButton')}
            onAdd={() => openDialog()}
            disabled={loading}
            spaced={!loading && surgeries.length > 0}
          >
            <MasterChartBackButton />
          </RecordActions>
        </>
      )}

      <Dialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {editingIndex >= 0 ? t('surgical.editTitle') : t('surgical.addTitle')}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Controller
              name="procedure"
              control={control}
              rules={{ required: 'surgical.required' }}
              render={({ field, fieldState: { error: fieldError } }) => (
                <TextField
                  {...field}
                  label={t('surgical.procedure')}
                  fullWidth
                  placeholder={t('surgical.procedurePlaceholder')}
                  error={!!fieldError}
                  helperText={fieldError?.message && t(fieldError.message)}
                />
              )}
            />

            <Controller
              name="year"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  label={t('surgical.year')}
                  type="number"
                  fullWidth
                  placeholder={String(new Date().getFullYear())}
                  inputProps={{ min: 1900, max: new Date().getFullYear() }}
                />
              )}
            />

            <Controller
              name="hospital"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  label={t('surgical.facility')}
                  fullWidth
                  placeholder={t('surgical.facilityPlaceholder')}
                />
              )}
            />

            <Controller
              name="notes"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  label={t('common.notes')}
                  fullWidth
                  multiline
                  rows={2}
                  placeholder={t('surgical.notesPlaceholder')}
                />
              )}
            />

            <Box>
              <Typography
                variant="caption"
                color="text.secondary"
                display="block"
                gutterBottom
              >
                {t('surgical.common')}
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                {COMMON_PROCEDURES.slice(0, 8).map((name) => (
                  <Chip
                    key={name}
                    label={name}
                    size="small"
                    variant="outlined"
                    onClick={() => setValue('procedure', name)}
                    sx={{ cursor: 'pointer' }}
                  />
                ))}
              </Box>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>{t('common.cancel')}</Button>
          <LoadingButton
            variant="contained"
            onClick={onDialogSubmit}
            loading={saving}
            disabled={saving}
          >
            {editingIndex >= 0 ? t('common.update') : t('common.add')}
          </LoadingButton>
        </DialogActions>
      </Dialog>

      <ConfirmDialog
        open={!!pendingDelete}
        onClose={() => setPendingDelete(null)}
        title={t('surgical.removeTitle')}
        message={t('common.removeConfirm', { label: pendingDelete?.label })}
        confirmLabel={t('common.remove')}
        cancelLabel={t('common.cancel')}
        confirmColor="error"
        loading={deleting}
        onConfirm={handleDelete}
        onExited={() => setPendingDelete(null)}
      />
    </div>
  );
};

export default SurgicalHistoryPage;
